import PlaceIcon from "@mui/icons-material/Place";
import {
  Box,
  Divider,
  Link,
  Stack,
  Typography,
} from "@mui/material";

const pick = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value !== null && value !== undefined && value !== "") return value;
  }
  return null;
};

const display = (value, fallback = "—") => {
  if (value === null || value === undefined || value === "") return fallback;
  if (value === 0 || value === "0") return fallback;
  if (typeof value === "boolean") return value ? "بله" : "خیر";
  return value;
};

/** Compact label:value cell — horizontal on sm+, stacked on xs */
const Field = ({ label, value, href, dir, emphasize = false }) => {
  const shown = display(value);
  const content =
    href && shown !== "—" ? (
      <Link href={href} underline="hover" dir={dir} variant="body2" fontWeight={600}>
        {shown}
      </Link>
    ) : (
      <Typography
        variant="body2"
        fontWeight={emphasize ? 700 : 600}
        dir={dir}
        sx={{ wordBreak: "break-word", lineHeight: 1.35 }}
      >
        {shown}
      </Typography>
    );

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "7.5rem 1fr" },
        columnGap: 1,
        rowGap: 0.15,
        alignItems: "baseline",
        minWidth: 0,
        py: 0.35,
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.3, whiteSpace: { sm: "nowrap" } }}
      >
        {label}
      </Typography>
      {content}
    </Box>
  );
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="caption"
    fontWeight={700}
    color="text.secondary"
    sx={{
      letterSpacing: 0.2,
      textTransform: "none",
      display: "block",
      mb: 0.5,
    }}
  >
    {children}
  </Typography>
);

/**
 * Read-only case context: applicant, location, plant, subscription bill, notes.
 * Dense layout for supervisor / operator forms.
 */
const AssignmentCaseInfoPanel = ({ item, dense = true }) => {
  if (!item) return null;

  const firstName = pick(item, "applicantFirstName", "ApplicantFirstName");
  const lastName = pick(item, "applicantLastName", "ApplicantLastName");
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "—";
  const mobile = pick(item, "applicantMobile", "ApplicantMobile");
  const telephone = pick(item, "applicantTelephone", "ApplicantTelephone");
  const email = pick(item, "applicantEmail", "ApplicantEmail");
  const province = pick(item, "provinceName", "ProvinceName");
  const city = pick(item, "cityName", "CityName");
  const address = pick(item, "address", "Address");
  const postalCode = pick(item, "postalCode", "PostalCode");
  const latitude = pick(item, "latitude", "Latitude");
  const longitude = pick(item, "longitude", "Longitude");
  const hasMap =
    Number(latitude) &&
    Number(longitude) &&
    Math.abs(Number(latitude)) > 0.0001 &&
    Math.abs(Number(longitude)) > 0.0001;
  const mapUrl = hasMap
    ? `https://www.google.com/maps?q=${Number(latitude)},${Number(longitude)}`
    : null;

  const applicantNote = pick(item, "applicantNote", "ApplicantNote");
  const adminComment = pick(item, "adminComment", "AdminComment");
  const referralNote = pick(item, "note", "Note");
  const hasNotes = Boolean(applicantNote || adminComment || referralNote);

  const gap = dense ? 1 : 1.5;

  return (
    <Stack spacing={dense ? 1.25 : 2} divider={<Divider flexItem />}>
      <Box>
        <SectionTitle>متقاضی و تماس</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1.2fr 0.9fr 0.9fr 1.2fr",
            },
            columnGap: gap,
            rowGap: 0.25,
          }}
        >
          <Field label="نام" value={fullName} emphasize />
          <Field label="موبایل" value={mobile} href={mobile ? `tel:${mobile}` : null} dir="ltr" />
          <Field
            label="تلفن"
            value={telephone}
            href={telephone ? `tel:${telephone}` : null}
            dir="ltr"
          />
          <Field label="ایمیل" value={email} dir="ltr" />
        </Box>
      </Box>

      <Box>
        <SectionTitle>محل نصب</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "0.8fr 0.8fr 0.9fr 1.5fr",
            },
            columnGap: gap,
            rowGap: 0.25,
          }}
        >
          <Field label="استان" value={province} />
          <Field label="شهر" value={city} />
          <Field label="کد پستی" value={postalCode} dir="ltr" />
          <Field label="آدرس" value={address} />
        </Box>
        {hasMap ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.5 }}>
            <PlaceIcon sx={{ fontSize: 16 }} color="primary" />
            <Link
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              underline="hover"
            >
              نقشه ({Number(latitude).toFixed(5)}, {Number(longitude).toFixed(5)})
            </Link>
          </Stack>
        ) : null}
      </Box>

      <Box>
        <SectionTitle>نیروگاه و اشتراک</SectionTitle>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "repeat(3, 1fr)",
            },
            columnGap: gap,
            rowGap: 0.25,
          }}
        >
          <Field
            label="ظرفیت"
            value={pick(item, "plantCapacityTitle", "PlantCapacityTitle")}
            emphasize
          />
          <Field
            label="نوع نیروگاه"
            value={pick(item, "powerPlantTypeTitle", "PowerPlantTypeTitle")}
          />
          <Field
            label="کاربری"
            value={pick(item, "locationUsageTypeTitle", "LocationUsageTypeTitle")}
          />
          <Field
            label="مالکیت"
            value={pick(item, "ownershipStatusTitle", "OwnershipStatusTitle")}
          />
          <Field
            label="نوع نصب"
            value={pick(item, "installationTypeTitle", "InstallationTypeTitle")}
          />
          <Field
            label="مساحت (م²)"
            value={pick(
              item,
              "installableAreaSquareMeter",
              "InstallableAreaSquareMeter"
            )}
            emphasize
          />
          <Field label="تعداد واحد" value={pick(item, "unitCount", "UnitCount")} />
          <Field label="تسهیلات" value={pick(item, "needsLoan", "NeedsLoan")} />
          <Field
            label="شناسه قبض"
            value={pick(item, "electricBoillNumber", "ElectricBoillNumber")}
            dir="ltr"
            emphasize
          />
        </Box>
      </Box>

      {hasNotes ? (
        <Box>
          <SectionTitle>یادداشت‌ها</SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              columnGap: gap,
              rowGap: 0.25,
            }}
          >
            {applicantNote ? (
              <Field label="متقاضی" value={applicantNote} />
            ) : null}
            {adminComment ? (
              <Field label="کارشناس" value={adminComment} />
            ) : null}
            {referralNote ? (
              <Box sx={{ gridColumn: { md: "1 / -1" } }}>
                <Field label="ارجاع" value={referralNote} />
              </Box>
            ) : null}
          </Box>
        </Box>
      ) : null}
    </Stack>
  );
};

export const getCaseContactActions = (item) => {
  if (!item) return { mobile: null, mapUrl: null };

  const mobile = pick(item, "applicantMobile", "ApplicantMobile");
  const latitude = pick(item, "latitude", "Latitude");
  const longitude = pick(item, "longitude", "Longitude");
  const hasMap =
    Number(latitude) &&
    Number(longitude) &&
    Math.abs(Number(latitude)) > 0.0001 &&
    Math.abs(Number(longitude)) > 0.0001;
  const mapUrl = hasMap
    ? `https://www.google.com/maps?q=${Number(latitude)},${Number(longitude)}`
    : null;

  return { mobile, mapUrl };
};

export const getCaseReferenceId = (item) =>
  pick(
    item,
    "requestReferenceId",
    "RequestReferenceId",
    "requestId",
    "RequestId"
  );

export default AssignmentCaseInfoPanel;
